/**
 * Crew Event Detail Page
 *
 * Shows event details for crew members including:
 * - Event info (name, venue, date, time)
 * - Timeline
 * - Role assignments (who's working what)
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvent, type Event } from '../../api/events';
import { listEventDocuments, type EventDocument } from '../../api/documents';
import { getMyAvailability, type Availability } from '../../api/availability';
import { getErrorMessage } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { TimelineList } from '../../components/timeline';
import { DocumentList } from '../../components/documents';
import SetAvailabilityModal from '../../components/availability/SetAvailabilityModal';
import AvailabilityNoteModal from '../../components/availability/AvailabilityNoteModal';
import { AVAILABILITY_STATUS } from '../../config/statusStyles';
import { formatLongDate, formatTimeWithZone } from '../../utils/dateFormatting';

export default function CrewEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // If the user landed here from /crew/assignments (or anywhere with history),
  // pop the stack on Back. Otherwise fall back to the dashboard.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/crew');
  };
  const [event, setEvent] = useState<Event | null>(null);
  const [documents, setDocuments] = useState<EventDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // My availability for THIS event, so the page shows what was already
  // chosen (from the dashboard or here) and lets it be changed in place.
  const [myAvailability, setMyAvailability] = useState<Availability | null>(null);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const loadMyAvailability = useCallback(async () => {
    if (!id) return;
    try {
      const { availability } = await getMyAvailability();
      setMyAvailability(availability.find((a) => a.eventId === id) ?? null);
    } catch {
      // Non-critical — the card just shows "not set".
    }
  }, [id]);

  useEffect(() => {
    loadMyAvailability();
  }, [loadMyAvailability]);

  useEffect(() => {
    if (!id) return;

    const loadEvent = async () => {
      try {
        const { event } = await getEvent(id);
        setEvent(event);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    const loadDocuments = async () => {
      try {
        const { documents } = await listEventDocuments(id);
        setDocuments(documents);
      } catch {
        // Silently fail for documents - not critical
      } finally {
        setDocumentsLoading(false);
      }
    };

    loadEvent();
    loadDocuments();
  }, [id]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-48 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Event</h2>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          type="button"
          onClick={handleBack}
          className="text-primary-600 hover:text-primary-500"
        >
          Back
        </button>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Event Not Found</h2>
        <button
          type="button"
          onClick={handleBack}
          className="text-primary-600 hover:text-primary-500"
        >
          Back
        </button>
      </div>
    );
  }

  const formattedDate = formatLongDate(event.eventDate);

  // Find current user's assignment
  const myAssignment = event.roles?.flatMap(role =>
    role.assignments?.filter(a => a.user.id === user?.id).map(a => ({
      ...a,
      roleName: role.roleName,
    })) || []
  )[0];

  return (
    <div className="space-y-6">
      {/* Back link — uses history fallback so a user who navigated here from
          /crew/assignments returns there instead of always going to /crew */}
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Event Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-xl font-bold text-gray-900">{event.name}</h1>
        <p className="text-gray-600 mt-1">
          {event.venue}
          {event.network ? ` • ${event.network}` : ''}
        </p>

        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center text-gray-600">
            <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {formattedDate}
          </div>
          {event.sport && (
            <div className="flex items-center text-gray-600">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {event.sport}
            </div>
          )}
        </div>

        {/* Time Details */}
        {(event.callTime || event.startTime || event.endTime) && (
          <div className="mt-4 grid grid-cols-3 gap-4 p-3 bg-gray-50 rounded-lg text-sm">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Crew Call</p>
              <p className="font-medium text-gray-900">
                {event.callTime ? formatTimeWithZone(event.callTime) : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Start</p>
              <p className="font-medium text-gray-900">
                {event.startTime ? formatTimeWithZone(event.startTime) : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">End</p>
              <p className="font-medium text-gray-900">
                {event.endTime ? formatTimeWithZone(event.endTime) : '-'}
              </p>
            </div>
          </div>
        )}

        {/* Your Role */}
        {myAssignment && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <span className="font-medium">Your Role:</span> {myAssignment.roleName}
            </p>
          </div>
        )}

        {/* Your availability + note — visible and changeable right here, not
            only from the dashboard. The status row hides once assigned (the
            assignment is the answer at that point); the note stays editable
            even then, and even inside the 24h status lockout. */}
        {event.status !== 'CANCELLED' && event.status !== 'COMPLETED' && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
            {!myAssignment && (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-gray-700 font-medium">Your availability:</span>
                  {myAvailability?.status ? (
                    <span
                      className={`px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${AVAILABILITY_STATUS[myAvailability.status].className}`}
                    >
                      {AVAILABILITY_STATUS[myAvailability.status].label}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">not set</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setAvailabilityModalOpen(true)}
                  className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-md text-primary-600 bg-primary-50 hover:bg-primary-100"
                >
                  {myAvailability?.status ? 'Change' : 'Set availability'}
                </button>
              </div>
            )}
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm min-w-0 flex-1">
                <span className="text-gray-700 font-medium">Your note:</span>{' '}
                {myAvailability?.notes ? (
                  <span className="text-gray-600 italic">{myAvailability.notes}</span>
                ) : (
                  <span className="text-gray-500">none</span>
                )}
              </p>
              <button
                type="button"
                onClick={() => setNoteModalOpen(true)}
                className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-md text-primary-600 bg-primary-50 hover:bg-primary-100"
              >
                {myAvailability?.notes ? 'Edit Note' : 'Add Note'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      {event.timelineEntries && event.timelineEntries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
          <TimelineList entries={event.timelineEntries} />
        </div>
      )}

      {/* Crew Assignments */}
      {event.roles && event.roles.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Crew</h2>
          <div className="space-y-4">
            {event.roles.map((role) => (
              <div key={role.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  {role.roleName}
                  <span className="text-gray-400 font-normal ml-2">
                    ({role.assignments?.length || 0}/{role.requiredCount})
                  </span>
                </h3>
                {role.assignments && role.assignments.length > 0 ? (
                  <div className="space-y-2">
                    {role.assignments.map((assignment) => {
                      const isMe = assignment.user.id === user?.id;
                      return (
                        <div
                          key={assignment.id}
                          className={`flex items-center gap-2 text-sm ${isMe ? 'text-green-700 font-medium' : 'text-gray-600'}`}
                        >
                          <span className={`w-2 h-2 ${isMe ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          {assignment.user.firstName} {assignment.user.lastName}
                          {isMe && <span className="text-xs text-green-600">(You)</span>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No one assigned yet</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      {(documents.length > 0 || documentsLoading) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
          <DocumentList documents={documents} loading={documentsLoading} />
        </div>
      )}

      {/* Additional Info */}
      {(event.tocInfo || event.engineeringNotes || event.additionalDetails) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
          <div className="space-y-4">
            {event.tocInfo && (
              <div>
                <h3 className="text-sm font-medium text-gray-700">TOC Information</h3>
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{event.tocInfo}</p>
              </div>
            )}
            {event.engineeringNotes && (
              <div>
                <h3 className="text-sm font-medium text-gray-700">Engineering Notes</h3>
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{event.engineeringNotes}</p>
              </div>
            )}
            {event.additionalDetails && (
              <div>
                <h3 className="text-sm font-medium text-gray-700">Additional Details</h3>
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{event.additionalDetails}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Availability modal — key remounts it per open so it picks up the
          latest current status (the modal seeds state from props on mount).
          The 24h-lockout rule is enforced server-side and surfaces as the
          modal's error message. */}
      {availabilityModalOpen && (
        <SetAvailabilityModal
          key={myAvailability?.status ?? 'unset'}
          isOpen={availabilityModalOpen}
          onClose={() => setAvailabilityModalOpen(false)}
          onSuccess={loadMyAvailability}
          eventId={event.id}
          eventName={event.name}
          currentStatus={myAvailability?.status ?? null}
          currentNotes={myAvailability?.notes ?? null}
        />
      )}

      {/* Note modal — mounted per open so it seeds from the latest note */}
      {noteModalOpen && (
        <AvailabilityNoteModal
          isOpen
          onClose={() => setNoteModalOpen(false)}
          onSuccess={loadMyAvailability}
          eventId={event.id}
          eventName={event.name}
          currentNote={myAvailability?.notes ?? null}
        />
      )}
    </div>
  );
}
